import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { DataTableBodyCellDate } from '@/components/custom';
import type { IUserPush, TUserPushStatus } from '@/interfaces';
import DataTableBodySkeleton from './DataTableBodySkeleton';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

const STATUS_LABELS: Record<TUserPushStatus, string> = {
	DELIVERED: 'Livré',
	SENT: 'Envoyé',
	FAILED: 'Échec',
	EXPIRED: 'Expiré',
};

const STATUS_STYLES: Record<TUserPushStatus, string> = {
	DELIVERED: 'bg-green-100 text-green-800',
	SENT: 'bg-blue-100 text-blue-800',
	FAILED: 'bg-red-100 text-red-800',
	EXPIRED: 'bg-gray-100 text-gray-800',
};

const PLATFORM_LABELS: Record<NonNullable<IUserPush['platform']>, string> = {
	ios: 'iOS',
	android: 'Android',
};

const PushStatus = ({ push, className }: { push: IUserPush; className?: string }) => (
	<div className={cn('flex flex-col gap-0.5', className)}>
		<Badge variant="outline" className={cn('w-fit border-0', STATUS_STYLES[push.status])}>
			{STATUS_LABELS[push.status] ?? push.status}
		</Badge>
		{push.error && <span className="text-xs text-destructive">{push.error}</span>}
	</div>
);

type TProps = {
	pushes: IUserPush[];
	isLoading?: boolean;
};

const DataTable = ({ pushes, isLoading }: TProps) => (
	<div className="space-y-5">
		<div className="hidden md:block border rounded-md">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-[150px]">Date</TableHead>
						<TableHead>Type</TableHead>
						<TableHead className="w-[100px]">Plateforme</TableHead>
						<TableHead className="w-[180px]">Statut</TableHead>
						<TableHead className="w-[150px]">Ouvert</TableHead>
					</TableRow>
				</TableHeader>
				{isLoading ? (
					<DataTableBodySkeleton rows={8} />
				) : (
					<TableBody>
						{pushes.map(push => (
							<TableRow key={push._id} className="h-12 hover:bg-muted/50 transition-colors">
								<DataTableBodyCellDate className="w-[150px] text-nowrap font-mono" date={new Date(push.createdAt)} format={DATE_FORMAT} />
								<TableCell className="py-1 font-mono text-xs">{push.type}</TableCell>
								<TableCell className="w-[100px] py-1 text-sm">{push.platform ? PLATFORM_LABELS[push.platform] : '-'}</TableCell>
								<TableCell className="w-[180px] py-1"><PushStatus push={push} /></TableCell>
								<TableCell className="w-[150px] py-1 text-sm text-muted-foreground font-mono text-nowrap">
									{push.openedAt ? dayjs(push.openedAt).format(DATE_FORMAT) : '-'}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</Table>
		</div>
		<div className="block md:hidden">
			{isLoading ? (
				<DataTableMobileSkeleton count={5} />
			) : (
				<div className="space-y-4">
					{pushes.map(push => (
						<div key={push._id} className="p-4 border rounded-lg bg-card text-card-foreground shadow-xs">
							<div className="flex items-start justify-between gap-3">
								<div className="space-y-1 min-w-0">
									<p className="font-mono text-xs truncate">{push.type}</p>
									<p className="text-xs text-muted-foreground">{push.platform ? PLATFORM_LABELS[push.platform] : '-'}</p>
								</div>
								<div className="flex flex-col items-end gap-1 text-[11px] text-muted-foreground">
									<PushStatus push={push} className="items-end" />
									<span>{dayjs(push.createdAt).format(DATE_FORMAT)}</span>
									{push.openedAt && <span>Ouvert {dayjs(push.openedAt).format(DATE_FORMAT)}</span>}
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	</div>
);

export default DataTable;
